import { Badge, createTheme, CSSVariablesResolver, FileInput, InputBase, TextInput } from '@mantine/core';

export const theme = createTheme({
  fontFamily: 'Onest, sans-serif',
  cursorType: 'pointer',
  activeClassName: 'active',
  fontSizes: {
    xxs: '10px',
    xs: '12px', // 0.75rem * 16px
    sm: '14px', // 0.875rem * 16px
    md: '16px', // 1rem * 16px
    lg: '18px', // 1.125rem * 16px
    xl: '20px', // 1.25rem * 16px
    xxl: '24px',
  },
  lineHeights: {
    xxs: '14px',
    xs: '20px',
    sm: '24px',
    md: '26px',
    lg: '32px',
    xl: '36px',
    xxl: '48px',
  },
  spacing: {
    xxs: '4px',
    xs: '10px', // 0.625rem * 16px
    sm: '12px', // 0.75rem * 16px
    md: '16px', // 1rem * 16px
    lg: '20px', // 1.25rem * 16px
    xl: '32px', // 2rem * 16px
    xxl: '40px',
  },
  radius: {
    xs: '2px',
    sm: '4px',
    md: '6px',
    lg: '10px',
    xl: '12px',
  },
  headings: {
    sizes: {
      h2: {
        fontSize: '32px',
        lineHeight: '40px',
      },
      h3: {
        fontSize: '24px',
        lineHeight: '32px',
      },
      h4: {
        fontSize: '20px',
        lineHeight: '24px',
      },
      h5: {
        fontSize: '16px',
        lineHeight: '20px',
      },
    },
  },
  components: {
    TextInput: TextInput.extend({
      vars: (theme, props) => {
        const result: Record<'root' | 'label' | 'wrapper' | 'input' | 'error', Record<string, string>> = {
          wrapper: {},
          root: {},
          label: {},
          input: {},
          error: {},
        };

        if (props.size === 'sm') {
          result.input['--input-height'] = '32px';
          result.input['--input-size'] = '32px';
          result.input['--input-fz'] = theme.fontSizes.sm;
          result.input['--input-radius'] = theme.radius.sm;
          result.wrapper['--input-margin-top'] = theme.spacing.none;
          result.wrapper['--input-margin-bottom'] = props.error ? theme.spacing.xs : theme.spacing.none;
          result.error['--input-error-size'] = theme.fontSizes.sm;
        }

        if (props.size === 'md') {
          result.input['--input-height'] = '40px';
          result.input['--input-size'] = '40px';
          result.input['--input-radius'] = theme.radius.sm;
          result.wrapper['--input-margin-top'] = theme.spacing.none;
          result.wrapper['--input-margin-bottom'] = props.error ? theme.spacing.xs : theme.spacing.none;
          result.error['--input-error-size'] = theme.fontSizes.xs;
        }

        if (props.size === 'lg') {
          result.input['--input-fz'] = theme.fontSizes.xxl;
          result.input['--input-radius'] = theme.radius.sm;
          result.wrapper['--input-margin-top'] = theme.spacing.none;
          result.wrapper['--input-margin-bottom'] = props.error ? theme.spacing.xs : theme.spacing.none;
          result.error['--input-error-size'] = theme.fontSizes.xxl;
        }

        return result;
      },
    }),
    FileInput: FileInput.extend({
      vars: (theme, props) => {
        const result: Record<'root' | 'label' | 'wrapper' | 'input' | 'error', Record<string, string>> = {
          wrapper: {},
          root: {},
          label: {},
          input: {},
          error: {},
        };

        if (props.size === 'sm') {
          result.input['--input-height'] = '32px';
          result.input['--input-size'] = '32px';
          result.input['--input-fz'] = theme.fontSizes.sm;
          result.input['--input-radius'] = theme.radius.sm;
          result.wrapper['--input-margin-top'] = theme.spacing.none;
          result.wrapper['--input-margin-bottom'] = props.error ? theme.spacing.xs : theme.spacing.none;
          result.error['--input-error-size'] = theme.fontSizes.sm;
        }

        return result;
      },
    }),
    InputBase: InputBase.extend({
      vars: (theme, props) => {
        const result: Record<'root' | 'label' | 'wrapper' | 'input' | 'error', Record<string, string>> = {
          wrapper: {},
          root: {},
          label: {},
          input: {},
          error: {},
        };

        if (props.size === 'sm') {
          result.input['--input-height'] = '32px';
          result.input['--input-size'] = '32px';
          result.input['--input-fz'] = theme.fontSizes.sm;
          result.input['--input-radius'] = theme.radius.sm;
          result.wrapper['--input-margin-top'] = theme.spacing.none;
          result.wrapper['--input-margin-bottom'] = props.error ? theme.spacing.xs : theme.spacing.none;
          result.error['--input-error-size'] = theme.fontSizes.sm;
        }

        return result;
      },
    }),
    Badge: Badge.extend({
      vars: (theme, props) => {
        const result: Record<'root' | 'label' | 'section', Record<string, string>> = {
          root: {},
          label: {},
          section: {},
        };

        result.root['--badge-fz-xs'] = theme.fontSizes.xxs;

        if (props.radius === 'sm') {
          result.root['--badge-radius'] = '4px';
        }

        const BADGE_COLORS: Record<string, { color: string; bg: string }> = {
          gray: { color: '#212529', bg: '#e9ecef' },
          indigo: { color: '#FFFFFF', bg: '#4263eb' },
          teal: { color: '#FFFFFF', bg: '#12b886' },
          orange: { color: '#FFFFFF', bg: '#fab005' },
        };

        const colorVars = BADGE_COLORS[props.color ?? ''];

        if (colorVars) {
          result.root['--badge-color'] = colorVars.color;
          result.root['--badge-bg'] = colorVars.bg;
        }

        return result;
      },
    }),
  },
});

export const cssVariablesResolver: CSSVariablesResolver = () => {
  return {
    variables: {},
    dark: {},
    light: {},
  };
};
